import React from "react";
import { useNavigate } from "react-router-dom";
import { Factory, ChevronRight, Zap } from "lucide-react";
import { usePoll } from "@/hooks/usePoll";
import { endpoints } from "@/lib/api";
import { PageHeader, Loading, StatusBadge } from "@/components/common";
import { num, inr, inrL, statusOf } from "@/lib/format";

function MimicNode({ node, onClick }) {
  const navigate = useNavigate();
  const s = statusOf(node.status);
  const impacts = node.impacted_by || [];
  // Accent the card with the most severe impacting utility's color.
  const accent = impacts.length ? statusOf(impacts[0].status).color : null;
  return (
    <button
      onClick={onClick}
      data-testid={`mimic-node-${node.id}`}
      className="relative bg-white border-2 rounded-md p-3 w-[160px] shrink-0 text-left transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
      style={{ borderColor: s.color, boxShadow: accent ? `inset 3px 0 0 0 ${accent}` : undefined }}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
        <StatusBadge status={node.status} />
      </div>
      <div className="text-[13px] font-semibold text-slate-900 leading-tight mb-2 h-8">{node.name}</div>
      {impacts.length > 0 && (
        <div className="mb-2 space-y-1">
          {impacts.map((u) => (
            <span
              key={u.twin}
              role="button"
              tabIndex={0}
              data-testid={`impact-${node.id}-${u.twin}`}
              onClick={(e) => { e.stopPropagation(); navigate(`/twin/${u.twin}`); }}
              className={`flex items-center gap-1 text-[10px] font-semibold rounded px-1.5 py-1 cursor-pointer ${statusOf(u.status).bg}`}
              style={{ color: statusOf(u.status).color, border: `1px solid ${statusOf(u.status).color}` }}
              title={`Impacted by ${u.name} — click to view twin`}
            >
              <Zap size={11} /> {u.name}
              {u.impact_per_year > 0 && <span className="font-mono">· {inr(u.impact_per_year)}/yr</span>}
            </span>
          ))}
        </div>
      )}
      <div className="space-y-0.5 text-[11px]">
        <div className="flex justify-between"><span className="text-slate-400">Actual</span><span className="font-mono text-slate-700">{num(node.actual_lph)} LPH</span></div>
        <div className="flex justify-between"><span className="text-slate-400">Expected</span><span className="font-mono text-slate-500">{num(node.expected_lph)}</span></div>
        <div className="flex justify-between"><span className="text-slate-400">Dev</span><span className={`font-mono ${Math.abs(node.deviation_pct) > 1 ? "text-amber-600" : "text-slate-500"}`}>{node.deviation_pct}%</span></div>
        <div className="flex justify-between pt-1 border-t border-slate-100 mt-1"><span className="text-slate-400">Cost</span><span className="font-mono font-semibold text-emerald-600">{inrL(node.cost_per_l)}/L</span></div>
      </div>
    </button>
  );
}

function UtilityNode({ node, onClick }) {
  const s = statusOf(node.status);
  return (
    <button
      onClick={onClick}
      data-testid={`mimic-utility-${node.id}`}
      className="relative bg-white border-2 rounded-md p-3 w-[160px] shrink-0 text-left transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
      style={{ borderColor: s.color }}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
        <StatusBadge status={node.status} />
      </div>
      <div className="text-[13px] font-semibold text-slate-900 leading-tight mb-2 h-8">{node.name}</div>
      <div className="space-y-0.5 text-[11px]">
        <div className="flex justify-between"><span className="text-slate-400">Utility</span><span className="font-mono text-slate-500">feeds process</span></div>
        <div className="flex justify-between pt-1 border-t border-slate-100 mt-1"><span className="text-slate-400">Cost</span><span className="font-mono font-semibold text-emerald-600">{inrL(node.cost_per_l)}/L</span></div>
      </div>
    </button>
  );
}

export default function ProcessMimic() {
  const navigate = useNavigate();
  const { data } = usePoll(endpoints.processMimic, 5000);
  if (!data) return <Loading />;

  return (
    <div data-testid="process-mimic-page">
      <PageHeader icon={Factory} title="Dairy Process Mimic"
        subtitle="The living dairy — Receiving → Dispatch. Each block shows Actual / Expected / Deviation / Cost / Status." />

      <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm overflow-x-auto">
        <div className="flex flex-col items-start w-max mx-auto">
          
          {/* Top Tier (Nodes 0-4) */}
          <div className="flex items-center gap-2">
            {data.stages.slice(0, 5).map((node, i) => (
              <React.Fragment key={node.id}>
                <MimicNode node={node} onClick={() => node.twin && navigate(`/twin/${node.twin}`)} />
                {i < 4 && (
                  <div className="flex items-center shrink-0 px-1">
                    <svg width="24" height="20" viewBox="0 0 24 20">
                      <line x1="0" y1="10" x2="18" y2="10" stroke="#2563eb" strokeWidth="2" />
                      <polygon points="18,5 24,10 18,15" fill="#2563eb" />
                    </svg>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Wrap Around Arrow */}
          <div className="w-full h-[40px] relative my-1">
            <svg width="100%" height="100%" className="absolute top-0 left-0">
              {/* Center of last node (node width 160, gap 32) -> 160/2 = 80px from right edge of tier 1.
                  Total width of tier 1 = 5*160 + 4*32 = 800 + 128 = 928.
                  Right center = 928 - 80 = 848.
                  Left center = 80. */}
              <path d="M 848 0 L 848 20 L 80 20 L 80 35" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinejoin="round" />
              <polygon points="77,33 83,33 80,40" fill="#2563eb" />
            </svg>
          </div>

          {/* Bottom Tier (Nodes 5-8) */}
          <div className="flex items-center gap-2">
            {data.stages.slice(5).map((node, i) => (
              <React.Fragment key={node.id}>
                <MimicNode node={node} onClick={() => node.twin && navigate(`/twin/${node.twin}`)} />
                {i < 3 && (
                  <div className="flex items-center shrink-0 px-1">
                    <svg width="24" height="20" viewBox="0 0 24 20">
                      <line x1="0" y1="10" x2="18" y2="10" stroke="#2563eb" strokeWidth="2" />
                      <polygon points="18,5 24,10 18,15" fill="#2563eb" />
                    </svg>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

        </div>
      </div>

      {data.utilities?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm mt-4 overflow-x-auto">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-3">
            Utilities — feed the process
          </div>
          <div className="flex items-center gap-2">
            {data.utilities.map((node) => (
              <UtilityNode key={node.id} node={node} onClick={() => node.twin && navigate(`/twin/${node.twin}`)} />
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-4 text-xs text-slate-500">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border-2 border-emerald-500" /> Normal</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border-2 border-amber-500" /> Warning</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border-2 border-red-500" /> Economic loss</span>
        <span className="flex items-center gap-1.5 text-blue-600"><ChevronRight size={14} /> Click a stage with a twin to drill down</span>
      </div>
    </div>
  );
}
